import { usePreviousPage, useReduxState } from 'hooks';
import {
  CopyButton,
  FlexContainer,
  LinkButton,
  Owners,
  PublishedProductText
} from 'components/common';
import { ErrorPage } from 'components/error';
import {
  Page,
  PageContent,
  PageHeader,
  PageInnerContent,
  PageSidebar
} from 'components/page';
import { PathPicture } from '.';

const PathPage = ({ children, navActiveLink }) => {
  const {
    messages,
    pageState: { path },
    serverConfig: { adminUrl },
    theme
  } = useReduxState();

  const backLink = usePreviousPage({
    fallbackUrl: '/paths?type=administered'
  });

  if (!path) {
    return (
      <ErrorPage
        description={messages.errorPermissionMaybe}
        icon="tasks"
        title={messages.pathNotFound}
      />
    );
  }

  return (
    <Page activePathname="/paths">
      <PageHeader
        backLinkAs={backLink.as}
        backLinkHref={backLink.href}
        title={backLink.title}
      />
      <PageContent withSidebar={true}>
        <PageSidebar
          active={`/admin/paths/${path.id}${navActiveLink || ''}`}
          isWide={true}
          links={[
            {
              href: `/admin/paths/${path.id}`,
              icon: 'stream',
              title: messages.pathsSteps
            },
            {
              href: `/admin/paths/${path.id}/settings`,
              icon: 'cog',
              title: messages.commonSettings
            },
            {
              href: `/admin/paths/${path.id}/competencies`,
              icon: 'medal',
              title: messages.commonCompetencies
            },
            {
              href: `/admin/paths/${path.id}/publish`,
              icon: 'check-circle',
              title: messages.commonPublish
            },
            {
              href: `/admin/paths/${path.id}/share`,
              icon: 'user',
              title: messages.commonShare
            },
            {
              href: `/admin/paths/${path.id}/users`,
              icon: 'chart-line',
              title: messages.commonFollow
            },
            {
              href: `/admin/paths/${path.id}/events`,
              icon: 'list',
              title: messages.commonActivityLog
            },
            {
              href: `/admin/paths/${path.id}/manage`,
              icon: 'tools',
              title: messages.pagesManage
            }
          ]}
        >
          <div className="container">
            <FlexContainer style={{ alignItems: 'flex-start', gap: 10 }}>
              <div className="picture">
                <PathPicture
                  picture={path.picture}
                  picturePosition={path.picturePosition}
                  size={480}
                />
              </div>

              <div className="title">
                <div>{path.title}</div>

                <div className="info">
                  <Owners owners={path.owners} />
                  <PublishedProductText
                    // Remount when the dates change, so the status is
                    // read against a fresh time after publishing or ending
                    key={`${path.startDate}-${path.endDate}`}
                    endDate={path.endDate}
                    startDate={path.startDate}
                  />
                </div>
              </div>
            </FlexContainer>

            <FlexContainer style={{ gap: 10, marginTop: 15 }}>
              <LinkButton
                category="lighter"
                href={`/paths/${path.id}`}
                size="small"
                text={messages.commonShowUserPage}
              />

              <CopyButton
                category="lighter"
                icon="link"
                successText={messages.commonCopied}
                size="small"
                tooltip={messages.commonCopyLink}
                value={`${adminUrl}/paths/${path.id}`}
              />
            </FlexContainer>
          </div>

          <style jsx>{`
            .container {
              border-bottom: 1px solid ${theme.lightGreyDark};
              flex-grow: 1;
              flex-shrink: 1;
              padding: 15px;
            }
            .picture {
              border-radius: 6px;
              flex-grow: 0;
              flex-shrink: 0;
              margin-top: 3px;
              overflow: hidden;
              position: relative;
              width: 32px;
              height: 32px;
            }
            .title {
              color: ${theme.pageTextColor || theme.textColor};
              display: flex;
              flex-direction: column;
              flex-grow: 1;
              flex-shrink: 1;
              font-size: 1.5rem;
              font-weight: 700;
              line-height: 1.2;
            }
            .info {
              display: flex;
              flex-direction: column;
              color: ${theme.pageTextColorLighter || theme.textColorLighter};
              font-size: 1.3rem;
              font-weight: 600;
              line-height: 1.5;
            }
          `}</style>
        </PageSidebar>
        <PageInnerContent>{children}</PageInnerContent>
      </PageContent>
    </Page>
  );
};

export default PathPage;
